import { useRef, useState, useEffect } from "react";
import { Skull } from "lucide-react";

const HOLD_MS = 1000;

export default function Card({ card, onClick, isSpymaster, disabled, justRevealed }) {
  const { index, word, revealed, identity } = card;
  const [progress, setProgress] = useState(0);
  const holdRef = useRef({ start: 0, raf: 0, fired: false });

  const cancelHold = () => {
    if (holdRef.current.raf) cancelAnimationFrame(holdRef.current.raf);
    holdRef.current.start = 0;
    holdRef.current.fired = false;
    setProgress(0);
  };

  useEffect(() => () => cancelHold(), []);

  const tick = () => {
    if (!holdRef.current.start) return;
    const elapsed = Date.now() - holdRef.current.start;
    const p = Math.min(1, elapsed / HOLD_MS);
    setProgress(p);
    if (p >= 1) {
      if (!holdRef.current.fired) {
        holdRef.current.fired = true;
        onClick?.();
      }
      cancelHold();
      return;
    }
    holdRef.current.raf = requestAnimationFrame(tick);
  };

  const beginHold = (e) => {
    if (disabled || revealed || isSpymaster) return;
    e.preventDefault();
    holdRef.current.start = Date.now();
    holdRef.current.fired = false;
    holdRef.current.raf = requestAnimationFrame(tick);
  };

  const endHold = () => { cancelHold(); };

  let cls = "cs-card";
  let content = null;
  // Dynamic font sizing for long words (e.g. "TRIPULACION", "RESTAURANTE")
  const len = (word || "").length;
  const sizeBig = len <= 7 ? "text-[12px] sm:text-base" : len <= 10 ? "text-[10px] sm:text-sm" : "text-[9px] sm:text-xs";
  const sizeSmall = len <= 7 ? "text-[9px] sm:text-[11px]" : len <= 10 ? "text-[8px] sm:text-[10px]" : "text-[7px] sm:text-[9px]";
  if (revealed) {
    if (identity === "red") cls += " revealed-red";
    else if (identity === "blue") cls += " revealed-blue";
    else if (identity === "civilian") cls += " revealed-civilian";
    else if (identity === "assassin") cls += " revealed-assassin";
    if (justRevealed) cls += " just-revealed";
    if (identity === "assassin" && justRevealed) cls += " shake";
    content = (
      <div className="flex flex-col items-center justify-center h-full w-full gap-1 px-1">
        {identity === "assassin" && <Skull size={22} className="text-black" />}
        <span className={`font-heading ${sizeSmall} uppercase tracking-wider text-center leading-tight break-words`}>{word}</span>
      </div>
    );
  } else {
    if (isSpymaster) {
      if (identity === "red") cls += " hint-red";
      else if (identity === "blue") cls += " hint-blue";
      else if (identity === "civilian") cls += " hint-civilian";
      else if (identity === "assassin") cls += " hint-assassin";
    }
    content = (
      <div className="relative h-full w-full flex flex-col justify-between py-1 px-1 sm:py-2 sm:px-2">
        <span className={`rotate-180 self-center font-body font-semibold ${sizeSmall} tracking-wider uppercase opacity-60 leading-none text-center break-words`}>
          {word}
        </span>
        <span className={`self-center font-body font-bold ${sizeBig} tracking-wider uppercase leading-none text-center break-words`}>
          {word}
        </span>
        {/* Long-press progress indicator */}
        {/* progress indicator moved to border */}
      </div>
    );
  }

  // Spymasters and disabled cards: regular click handler (or none)
  // Agents: long-press required to reveal
  const isHoldable = !disabled && !revealed && !isSpymaster;

  return (
    <button
      data-testid={`card-${index}`}
      data-card-index={index}
      onContextMenu={(e) => e.preventDefault()}
      onPointerDown={isHoldable ? beginHold : undefined}
      onPointerUp={isHoldable ? endHold : undefined}
      onPointerLeave={isHoldable ? endHold : undefined}
      onPointerCancel={isHoldable ? endHold : undefined}
      onClick={(e) => {
        // Block accidental clicks for agents — long press is required
        if (isHoldable) { e.preventDefault(); return; }
      }}
      disabled={disabled || revealed}
      className={cls + " w-full h-full select-none touch-none"}
      style={{ aspectRatio: "1 / 1", boxShadow: progress > 0 ? `0 0 20px 12px rgba(245,158,11,${0.2 + 0.8 * progress})` : undefined }}
      aria-label={`Carta ${index + 1}: ${word}`}
    >
      {content}
    </button>
  );
}
